"use client";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { IChat, IConfig, IProject, IUser } from "@/lib/types";
import { ChatsContext, UserContext, ConfigContext, ProjectsContext, ErrorContext, IApiError } from "./context";
import {
    CHATS_PER_PAGE,
    getChats,
    getProjects,
    loadConfig,
    saveConfig,
    updateChat as daoUpdateChat,
    deleteChat as daoDeleteChat,
    createProject as daoCreateProject,
    updateProject as daoUpdateProject,
    deleteProject as daoDeleteProject,
} from "@/lib/dao";
import { getJWT } from "@/lib/auth";
import { setBugsnagUser } from "@/lib/bugsnag";
import { HEALTH_API_URL } from "@/lib/utils";

function isNetworkOrCorsError(message?: string): boolean {
    if (!message) return false;
    return (
        message.includes("Failed to fetch") ||
        message.includes("Load failed") ||
        message.includes("NetworkError") ||
        message.includes("CORS")
    );
}

export const Providers = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = React.useState<IUser | undefined>(undefined);
    const [chats, setChats] = React.useState<IChat[]>([]);
    const [projects, setProjects] = React.useState<IProject[]>([]);
    const [config, setConfig] = React.useState<IConfig>(loadConfig());
    const [errors, setErrors] = React.useState<IApiError[]>([]);
    const networkWarningRef = useRef(false);
    const errorCounterRef = useRef(0);

    const { instance, accounts } = useMsal();

    const addError = useCallback((error: IApiError) => {
        console.error(`[API Error] ${error.type}:`, error.message, error.details || "");
        setErrors((prev) => [...prev, error]);
        // Auto-dismiss after 10 seconds (except warnings, which persist until manually closed)
        if (error.type !== "warning") {
            setTimeout(() => {
                setErrors((prev) => prev.filter((e) => e.timestamp !== error.timestamp));
            }, 10000);
        }
    }, []);

    const clearError = useCallback((timestamp: number) => {
        setErrors((prev) => prev.filter((e) => e.timestamp !== timestamp));
    }, []);

    const clearAllErrors = useCallback(() => {
        setErrors([]);
    }, []);

    const errorContextValue = useMemo(
        () => ({
            errors,
            addError,
            clearError,
            clearAllErrors,
        }),
        [errors, addError, clearError, clearAllErrors]
    );

    const updateUser = useCallback((inputUser: IUser | undefined) => {
        setUser(inputUser);
    }, []);

    const userContextValue = useMemo(
        () => ({
            user: user,
            updateUser: updateUser,
        }),
        [user, updateUser]
    );

    const updateChat = useCallback(
        async (inputChat: Partial<IChat>, doNetwork: boolean, doUpdateLastmessage = false) => {
            if (doUpdateLastmessage) {
                inputChat.lastmessage = new Date();
            }
            setChats((prev) => {
                const existingChat = prev.find((chat) => chat.chatid === inputChat.chatid);
                if (existingChat) {
                    console.log(`${Date.now()} existingChat in updateChat: ${JSON.stringify(existingChat)}`);
                    const updatedChat = { ...existingChat, ...inputChat };
                    return prev.map((chat) => (chat.chatid === inputChat.chatid ? updatedChat : chat));
                } else {
                    return [...prev, inputChat as IChat].sort(
                        (a, b) => b.lastmessage.getTime() - a.lastmessage.getTime()
                    );
                }
            });
            if (doNetwork && inputChat.chatid) {
                getJWT(instance, accounts).then((jwt) =>
                    daoUpdateChat(jwt, inputChat as Partial<IChat> & Pick<IChat, "chatid">)
                );
            }
        },
        [accounts, instance]
    );

    const deleteChat = useCallback(
        async (chatid: string) => {
            setChats((prev) => prev.filter((chat) => chat.chatid !== chatid));
            getJWT(instance, accounts).then((jwt) => daoDeleteChat(jwt, chatid));
        },
        [accounts, instance]
    );

    const chatsContextValue = useMemo(
        () => ({
            chats: chats,
            setChats: setChats,
            updateChat: updateChat,
            deleteChat: deleteChat,
        }),
        [chats, updateChat, deleteChat]
    );

    const createProject = useCallback(
        async (inputProject: Pick<IProject, "title" | "description">): Promise<IProject> => {
            const jwt = await getJWT(instance, accounts);
            const createdProject = await daoCreateProject(jwt, inputProject);
            setProjects([...projects, createdProject]);
            return createdProject;
        },
        [accounts, instance, projects]
    );

    const updateProject = useCallback(
        async (inputProject: Partial<IProject>) => {
            const updateProject = async (inputProject: Partial<IProject>) => {
                const existingProject = projects.find((project) => project.projectid === inputProject.projectid);
                if (existingProject) {
                    const updatedProject = { ...existingProject, ...inputProject };
                    setProjects(
                        projects.map((project) =>
                            project.projectid === inputProject.projectid ? updatedProject : project
                        )
                    );
                } else {
                    setProjects(
                        [...projects, inputProject as IProject].sort((a, b) =>
                            (a.title || "").localeCompare(b.title || "")
                        )
                    );
                }
                getJWT(instance, accounts).then((jwt) =>
                    daoUpdateProject(jwt, inputProject as Partial<IProject> & Pick<IProject, "projectid">)
                );
            };
            return updateProject(inputProject);
        },
        [accounts, instance, projects]
    );

    const deleteProject = useCallback(
        (projectid: string) => {
            const deleteProject = async (projectid: string) => {
                setProjects(projects.filter((project) => project.projectid !== projectid));
                // Remove chats from this project from the navbar list
                if (chats.find((chat) => chat.projectid === projectid)) {
                    setChats(chats.filter((chat) => chat.projectid !== projectid));
                }
                getJWT(instance, accounts).then((jwt) => daoDeleteProject(jwt, projectid));
            };
            return deleteProject(projectid);
        },
        [accounts, chats, instance, projects]
    );

    const projectsContextValue = useMemo(
        () => ({
            projects: projects,
            setProjects: setProjects,
            createProject: createProject,
            updateProject: updateProject,
            deleteProject: deleteProject,
        }),
        [projects, createProject, updateProject, deleteProject]
    );

    const updateConfig = (config: IConfig) => {
        setConfig(config);
        saveConfig(config);
    };

    const configContextValue = {
        config: config,
        updateConfig: updateConfig,
    };

    // Dev-only health check: detect when API is unreachable (e.g. not on VPN)
    useEffect(() => {
        if (process.env.NEXT_PUBLIC_ENV !== "development") return;

        let cancelled = false;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);

        fetch(HEALTH_API_URL, { signal: controller.signal })
            .then((res) => {
                if (!cancelled && !res.ok) {
                    networkWarningRef.current = true;
                    addError({
                        type: "warning",
                        message:
                            "You need to be connected to a Fender network for FAI to function properly. Please connect to the Fender VPN or office WiFi and try again.",
                        timestamp: Date.now(),
                    });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    networkWarningRef.current = true;
                    addError({
                        type: "warning",
                        message:
                            "You need to be connected to a Fender network for FAI to function properly. Please connect to the Fender VPN or office WiFi and try again.",
                        timestamp: Date.now(),
                    });
                }
            })
            .finally(() => {
                clearTimeout(timeout);
            });

        return () => {
            cancelled = true;
            controller.abort();
            clearTimeout(timeout);
        };
    }, [addError]);

    useEffect(() => {
        // Set the user from MSAL
        if (accounts.length === 0) {
            setUser(undefined);
        } else {
            setUser({
                userid: accounts[0].homeAccountId,
                name: accounts[0].name || "User",
                email: accounts[0].username,
                department: accounts[0].idTokenClaims?.department as string | undefined,
            });
            setBugsnagUser({
                id: accounts[0].homeAccountId,
                email: accounts[0].username,
                name: accounts[0].name,
            });
        }
        // Load the config from localstorage
        // We don't have to do this, it's done at the initial provider level
        setConfig(loadConfig());
        // Load the chats and projects from the network
        if (accounts.length > 0) {
            getJWT(instance, accounts)
                .then((jwt) => {
                    console.log("[Providers] Got JWT, fetching chats and projects");

                    getChats(jwt, 0, CHATS_PER_PAGE)
                        .then((chats) => {
                            console.log("[Providers] Successfully loaded chats:", chats.length);
                            setChats(chats);
                        })
                        .catch((error) => {
                            console.error("[Providers] Failed to load chats:", error);
                            const networkError = isNetworkOrCorsError(error.message);

                            // Suppress redundant network errors when network warning is already shown
                            if (networkError && networkWarningRef.current) return;

                            addError({
                                type: networkError ? "network" : "api",
                                message: networkError
                                    ? "Cannot reach the API server"
                                    : `Failed to load chats: ${error.message}`,
                                details: `Error: ${error.message}\n\nStack: ${error.stack || "No stack trace"}`,
                                timestamp: Date.now() + ++errorCounterRef.current,
                            });
                        });

                    getProjects(jwt)
                        .then((projects) => {
                            console.log("[Providers] Successfully loaded projects:", projects.length);
                            setProjects(projects);
                        })
                        .catch((error) => {
                            console.error("[Providers] Failed to load projects:", error);
                            const networkError = isNetworkOrCorsError(error.message);

                            // Suppress redundant network errors when network warning is already shown
                            if (networkError && networkWarningRef.current) return;

                            addError({
                                type: networkError ? "network" : "api",
                                message: networkError
                                    ? "Cannot reach the API server"
                                    : `Failed to load projects: ${error.message}`,
                                details: `Error: ${error.message}\n\nStack: ${error.stack || "No stack trace"}`,
                                timestamp: Date.now() + ++errorCounterRef.current,
                            });
                        });
                })
                .catch((error) => {
                    console.error("[Providers] Failed to get JWT:", error);
                    addError({
                        type: "auth",
                        message: "Failed to get authentication token",
                        details: `Error: ${error.message}\n\nStack: ${error.stack || "No stack trace"}`,
                        timestamp: Date.now(),
                    });
                });
        }
    }, [accounts, instance, addError]);

    return (
        <>
            <ErrorContext value={errorContextValue}>
                <UserContext value={userContextValue}>
                    <ProjectsContext value={projectsContextValue}>
                        <ChatsContext value={chatsContextValue}>
                            <ConfigContext value={configContextValue}>{children}</ConfigContext>
                        </ChatsContext>
                    </ProjectsContext>
                </UserContext>
            </ErrorContext>
        </>
    );
};
